import { FamilyPlanConfig } from 'fitbeat.gym-core';
import React from 'react';
import { translate } from 'react-i18next';
import {
  Keyboard,
  StyleSheet,
  Text,
  TouchableWithoutFeedback,
  View,
} from 'react-native';
import SmoothPinCodeInput from 'react-native-smooth-pincode-input';
import { connect } from 'react-redux';
import { Button } from '../components';
import theme from '../theme';
import { defaultScale, scaleText } from '../utils/scaling';
import UserDataConsumer from '../utils/UserDataConsumer';
import { pinAttemptChanged, selectMember, swopUser } from './actions';
import {
  isSwitchingToUserSelector, pinAttemptSelector, switchMemberFailureSelector,
} from './selector';
import auth from '@react-native-firebase/auth';
import { UserServiceClient } from 'fitbeat.api-clients';
import { getApiConfig } from '../config';
import { AuthTokenBuilder } from '../utils/authTokenBuilder';

interface IProps {
  swopUser: (code: string) => void;
  selectMember: (code: string) => void;
  t: (s: string) => string;
  isSwitchingToUser: boolean;
  shouldShowError: boolean;
  pinChanged: (pin: string) => void;
  code: string;
  navigation: any;
}

class EnterProfilePinComponent extends React.PureComponent<IProps> {
  private pinInput;
  constructor(props) {
    super(props);
    this.pinInput = React.createRef();
  }

  public componentDidMount() {
    this.props.pinChanged('');
  }

  public render() {
    const { t, isSwitchingToUser , pinChanged, shouldShowError, code} = this.props;

    return (
      <TouchableWithoutFeedback onPress={Keyboard.dismiss}>
        <View style={styles.container}>
          <View style={styles.pinContainer}>
            <Text style={styles.enterPinText}>{t('enterYourPin')}</Text>
            {shouldShowError && (
              <Text style={styles.incorrectPinText}>{t('incorrectPin')}</Text>
            )}
            <View
              style={[
                styles.textInputContainerStyle,
                shouldShowError && { borderColor: theme.RED },
              ]}
            >
              <SmoothPinCodeInput
                ref={this.pinInput}
                cellStyle={{
                  borderBottomWidth: 1,
                  borderColor: theme.GREY_MEDIUM_LIGHT,
                }}
                cellStyleFocused={{
                  borderColor: theme.GREY_MEDIUM,
                }}
                value={code}
                cellSpacing={10}
                onTextChange={(code) => {
                  pinChanged(code);
                }
                }
                animated={false}
                textStyle={[
                  styles.textInputStyle,
                  shouldShowError && { color: theme.RED },
                ]}
                mask={<Text style={styles.textInputStyle}>*</Text>}
                maskDelay={500}
                password={!shouldShowError}
                codeLength={FamilyPlanConfig.pinLength}
                keyboardType={'decimal-pad'}
                restrictToNumbers={true}
                autoFocus={true}
                onFulfill={this.changeProfilePin}
              />
            </View>
            {shouldShowError && (
              <Text
                style={styles.forgotPinText}
                onPress={() => { this.props.navigation.navigate('ForgotMyPin'); }}
              >
                {t('forgotPin')}
              </Text>
            )}
          </View>
          <Button
            title={t('Submit')}
            overrideFontStyles={styles.buttonText}
            onPress={this.changeProfilePin}
            isLoading={isSwitchingToUser}
            disabled={code.length !== FamilyPlanConfig.pinLength}
          />
        </View>
      </TouchableWithoutFeedback>
    );
  }
  private changeProfilePin = async () => {
    Keyboard.dismiss();
    setTimeout(() => {
    const { swopUser, selectMember, code, navigation } = this.props;
    if (navigation.getParam('enterPinFromSelectMember')) {
      selectMember(code);
    } else {
      swopUser(code)
    }
    }, 1);
  }
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: defaultScale(15),
    backgroundColor: theme.WHITE,

  },
  textInputStyle: {
    fontFamily: theme.Custom700,
    fontSize: 36,
    color: theme.BLACK,
  },
  textInputContainerStyle: {
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 28,
    borderColor: '#D6D6D6',
    borderWidth: 1,
    marginBottom: defaultScale(21),
  },
  incorrectPinText: {
    fontFamily: theme.Custom700,
    fontSize: scaleText(16),
    color: theme.RED,
    top: defaultScale(64),
    position: 'absolute',
  },
  buttonText: {
    fontFamily: theme.Custom700,
    fontSize: scaleText(16),
    color: theme.WHITE,
  },
  pinContainer: {
    flex: 1,
    position: 'relative',
  },
  enterPinText: {
    ...theme.textStyle.heading,
    marginBottom: defaultScale(64),
  },
  forgotPinText: {
    color: theme.PRIMARY,
    textAlign: 'center',
    fontFamily: theme.Custom700,
    fontSize: scaleText(16),
  },
});

const mapStateToProps = (state: any) => {
  return {
    isSwitchingToUser: isSwitchingToUserSelector(state),
    shouldShowError: switchMemberFailureSelector(state),
    code: pinAttemptSelector(state),
  };
};

const mapDispatchToProps = (dispatch: any, ownProps: any) => {
  const userServiceClient = new UserServiceClient(getApiConfig(), fetch, console.log, new AuthTokenBuilder());
  const userProfileService = ownProps.user?.userProfileService;
  const setUpInitialUserData = ownProps.user?.setUpInitialUserData;
  const userProfile = ownProps.user?.userProfile;
  const navigation = ownProps.navigation;
  return {
    swopUser: (code: string) =>
      dispatch(
        swopUser(
          navigation.getParam('swoppingToUser'),
          userProfile.uid,
          userServiceClient,
          userProfileService,
          navigation,
          setUpInitialUserData,
          code,
        ),
      ),
      selectMember: (code: string) => dispatch(selectMember(navigation.getParam('swoppingToUser'), auth().currentUser.uid, userServiceClient, navigation, code)),
      pinChanged: (pin: string) => dispatch(pinAttemptChanged(pin))
  };
};
export const EnterProfilePin = translate(['familyPlan'], { wait: true })(
  UserDataConsumer(connect(mapStateToProps, mapDispatchToProps)(EnterProfilePinComponent)),
);
